// src/pages/finance-portal/getting-started/FinanceGuide.js
//
// Finance Portal user guide: explains the end-to-end workflow from
// bank connection through reporting. Each step links into the page that
// performs that step so users can follow along.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Grid, Card, CardContent, Button, Chip,
    Stepper, Step, StepLabel, StepContent, List, ListItem, ListItemIcon,
    ListItemText, Divider, Stack,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleIcon from '@mui/icons-material/People';
import PaidIcon from '@mui/icons-material/Paid';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import TimelineIcon from '@mui/icons-material/Timeline';
import CalculateIcon from '@mui/icons-material/Calculate';
import GridOnIcon from '@mui/icons-material/GridOn';
import SpeedIcon from '@mui/icons-material/Speed';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import BuildIcon from '@mui/icons-material/Build';
import PieChartIcon from '@mui/icons-material/PieChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ArticleIcon from '@mui/icons-material/Article';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';
const INDIGO = '#4f46e5';

// ----------------------------------------------------------------------
// Workflow stages — ordered from setup to analytics
// ----------------------------------------------------------------------

const STAGES = [
    {
        id: 'setup',
        title: '1. Setup',
        description: 'Connect banks and define how money is classified.',
        color: '#0ea5e9',
        icon: LinkIcon,
        steps: [
            {
                label: 'Connect Bank Accounts',
                icon: LinkIcon,
                to: '/finance-portal/banking',
                summary: 'Link checking, savings, credit, and loan accounts via Stripe Financial Connections from the Banking Center. Balances, transactions, and balance history all live in one workspace.',
                actions: [
                    { label: 'Open Banking Center', to: '/finance-portal/banking' },
                ],
            },
            {
                label: 'Chart of Accounts',
                icon: AccountTreeIcon,
                to: '/finance-portal/chart-of-accounts',
                summary: 'Define the account tree (Assets 10xx, Liabilities 20xx, Equity 3xxx, Revenue 4xxx, Expenses 5xxx). You can apply a template.',
                actions: [
                    { label: 'Open Chart of Accounts', to: '/finance-portal/chart-of-accounts' },
                    { label: 'Set Fiscal Periods', to: '/finance-portal/period-closing' },
                    { label: 'Define Dimensions', to: '/finance-portal/dimensions' },
                ],
            },
            {
                label: 'Category Mapping',
                icon: SwapHorizIcon,
                to: '/finance-portal/category-mapping',
                summary: 'Teach the system how merchant categories map to GL accounts so new transactions code themselves.',
                actions: [
                    { label: 'Open Category Mapping', to: '/finance-portal/category-mapping' },
                    { label: 'Journal Mappings', to: '/finance-portal/journal-mappings' },
                ],
            },
        ],
    },
    {
        id: 'transactions',
        title: '2. Transactions & GL',
        description: 'Import, classify, and post activity to the ledger.',
        color: '#f59e0b',
        icon: SwapHorizIcon,
        steps: [
            {
                label: 'Review Bank Transactions',
                icon: SwapHorizIcon,
                to: '/finance-portal/banking',
                summary: 'Use the Banking Center For Review tab to categorize incoming transactions, bulk-post, exclude noise, and view balance history — all in one place.',
                actions: [
                    { label: 'Banking Center', to: '/finance-portal/banking' },
                    { label: 'Category Mapping', to: '/finance-portal/category-mapping' },
                ],
            },
            {
                label: 'Journal Entries',
                icon: MenuBookIcon,
                to: '/finance-portal/journal-entries',
                summary: 'Create manual double-entry journals, reverse posted entries, and run recurring journal schedules.',
                actions: [
                    { label: 'Journal Entries', to: '/finance-portal/journal-entries' },
                    { label: 'Recurring Journals', to: '/finance-portal/recurring-journals' },
                    { label: 'Journal Approvals', to: '/finance-portal/journal-approvals' },
                ],
            },
        ],
    },
    {
        id: 'ar',
        title: '3. Accounts Receivable',
        description: 'Bill customers and collect payments.',
        color: '#8b5cf6',
        icon: RequestQuoteIcon,
        steps: [
            {
                label: 'Customers & Estimates',
                icon: GroupsIcon,
                to: '/finance-portal/customers',
                summary: 'Maintain customer records, issue estimates, and convert them to invoices when accepted.',
                actions: [
                    { label: 'Customers', to: '/finance-portal/customers' },
                    { label: 'Estimates', to: '/finance-portal/estimates' },
                    { label: 'Invoice Templates', to: '/finance-portal/invoice-templates' },
                ],
            },
            {
                label: 'Invoices & Payments',
                icon: RequestQuoteIcon,
                to: '/finance-portal/invoices',
                summary: 'Issue invoices, share payment links, apply payments, credit memos, and statements.',
                actions: [
                    { label: 'Invoices', to: '/finance-portal/invoices' },
                    { label: 'Recurring Invoices', to: '/finance-portal/recurring-invoices' },
                    { label: 'Payment Links', to: '/finance-portal/payment-links' },
                    { label: 'Payments', to: '/finance-portal/payments' },
                    { label: 'Credit Memos', to: '/finance-portal/credit-memos' },
                    { label: 'Cash Application', to: '/finance-portal/cash-application' },
                    { label: 'Statements', to: '/finance-portal/statements' },
                    { label: 'AR Aging', to: '/finance-portal/ar-aging' },
                    { label: 'Revenue Recognition', to: '/finance-portal/revenue-recognition' },
                ],
            },
        ],
    },
    {
        id: 'ap',
        title: '4. Accounts Payable',
        description: 'Bill intake, approval, and payment.',
        color: '#ef4444',
        icon: ReceiptLongIcon,
        steps: [
            {
                label: 'Vendors & Bills',
                icon: PeopleIcon,
                to: '/finance-portal/vendors',
                summary: 'Maintain vendor records, capture bills from documents, and route them through approval.',
                actions: [
                    { label: 'Vendors', to: '/finance-portal/vendors' },
                    { label: 'Bills', to: '/finance-portal/bills' },
                ],
            },
            {
                label: 'Pay & Reconcile',
                icon: PaidIcon,
                to: '/finance-portal/bill-payments',
                summary: 'Approve payments, track AP aging, and reconcile against bank transactions.',
                actions: [
                    { label: 'Bill Payments', to: '/finance-portal/bill-payments' },
                    { label: 'AP Aging', to: '/finance-portal/ap-aging' },
                    { label: 'Expenses', to: '/finance-portal/expenses' },
                    { label: 'Expense Reports', to: '/finance-portal/expense-reports' },
                ],
            },
        ],
    },
    {
        id: 'payroll',
        title: '5. Payroll & People',
        description: 'Run payroll and file compliance reports.',
        color: '#14b8a6',
        icon: PeopleIcon,
        steps: [
            {
                label: 'Employees & Runs',
                icon: PeopleIcon,
                to: '/finance-portal/payroll-employees',
                summary: 'Onboard employees and contractors, configure pay frequencies and benefits, then run payroll.',
                actions: [
                    { label: 'Payroll Dashboard', to: '/finance-portal/payroll-dashboard' },
                    { label: 'Employees', to: '/finance-portal/payroll-employees' },
                    { label: 'Payroll Runs', to: '/finance-portal/payroll-runs' },
                    { label: 'Pay Stubs', to: '/finance-portal/pay-stubs' },
                    { label: 'Contractor Payments', to: '/finance-portal/contractor-payments' },
                    { label: 'Benefit Plans', to: '/finance-portal/benefit-plans' },
                ],
            },
            {
                label: 'Compliance',
                icon: LockIcon,
                to: '/finance-portal/compliance-filings',
                summary: 'Handle tax documents, new-hire reports, ACA compliance, and earned wage access.',
                actions: [
                    { label: 'Tax Documents', to: '/finance-portal/tax-documents' },
                    { label: 'Tax Filings', to: '/finance-portal/tax-filings' },
                    { label: 'New Hire Reports', to: '/finance-portal/new-hire-reports' },
                    { label: 'ACA Compliance', to: '/finance-portal/aca-compliance' },
                    { label: 'Earned Wage Access', to: '/finance-portal/earned-wage-access' },
                    { label: 'Compliance Filings', to: '/finance-portal/compliance-filings' },
                ],
            },
        ],
    },
    {
        id: 'tax',
        title: '6. Tax',
        description: 'Collect, file, and plan sales and income tax.',
        color: '#eab308',
        icon: CalculateIcon,
        steps: [
            {
                label: 'Sales & Income Tax',
                icon: CalculateIcon,
                to: '/finance-portal/sales-tax',
                summary: 'Configure sales-tax nexus, track exemptions, and compute income / estimated tax.',
                actions: [
                    { label: 'Sales Tax', to: '/finance-portal/sales-tax' },
                    { label: 'Tax Nexus', to: '/finance-portal/tax-nexus' },
                    { label: 'Tax Exemptions', to: '/finance-portal/tax-exemptions' },
                    { label: 'Income Tax', to: '/finance-portal/income-tax' },
                    { label: 'Estimated Tax', to: '/finance-portal/estimated-tax' },
                    { label: 'Depreciation', to: '/finance-portal/depreciation' },
                ],
            },
            {
                label: 'Returns & Planning',
                icon: ArticleIcon,
                to: '/finance-portal/tax-return-filings',
                summary: 'Organize documents, file returns, plan, and respond to notices.',
                actions: [
                    { label: 'Tax Doc Organizer', to: '/finance-portal/tax-doc-organizer' },
                    { label: 'Tax Return Filings', to: '/finance-portal/tax-return-filings' },
                    { label: 'Tax Planning', to: '/finance-portal/tax-planning' },
                    { label: 'Tax Notices', to: '/finance-portal/tax-notices' },
                    { label: 'Tax Audit Trail', to: '/finance-portal/tax-audit-trail' },
                    { label: 'Tax Settings', to: '/finance-portal/tax-settings' },
                ],
            },
        ],
    },
    {
        id: 'close',
        title: '7. Period Close',
        description: 'Seal the period so reports are final.',
        color: '#6366f1',
        icon: LockIcon,
        steps: [
            {
                label: 'Close the Period',
                icon: LockIcon,
                to: '/finance-portal/period-closing',
                summary: 'Run month-end reconciliation, journal approvals, reversals, and close the fiscal period.',
                actions: [
                    { label: 'Period Closing', to: '/finance-portal/period-closing' },
                    { label: 'Journal Approvals', to: '/finance-portal/journal-approvals' },
                    { label: 'Agent: Month-End Close', to: '/finance-portal/agents/month-end-close' },
                ],
            },
        ],
    },
    {
        id: 'reporting',
        title: '8. Reporting & BI',
        description: 'Generate statements and analyze performance.',
        color: ACCENT,
        icon: AssessmentIcon,
        steps: [
            {
                label: 'Standard Financial Statements',
                icon: TrendingUpIcon,
                to: '/finance-portal/reports/income-statement',
                summary: 'P&L, Balance Sheet, Cash Flow, Statement of Equity, Trial Balance, and GL Detail — all produced on demand from posted journals.',
                actions: [
                    { label: 'Income Statement (P&L)', to: '/finance-portal/reports/income-statement' },
                    { label: 'Balance Sheet', to: '/finance-portal/reports/balance-sheet' },
                    { label: 'Cash Flow Statement', to: '/finance-portal/reports/cash-flow' },
                    { label: 'Statement of Equity', to: '/finance-portal/reports/statement-of-equity' },
                    { label: 'Trial Balance', to: '/finance-portal/reports/trial-balance' },
                    { label: 'GL Detail', to: '/finance-portal/reports/gl-detail' },
                ],
            },
            {
                label: 'Management Reports',
                icon: CompareArrowsIcon,
                to: '/finance-portal/reports/budget-vs-actual',
                summary: 'Budget variance, segment and project profitability, customer profitability, KPI dashboards, and board packages.',
                actions: [
                    { label: 'Budget vs Actual', to: '/finance-portal/reports/budget-vs-actual' },
                    { label: 'Departmental P&L', to: '/finance-portal/reports/departmental-pnl' },
                    { label: 'Project Profitability', to: '/finance-portal/reports/project-profitability' },
                    { label: 'Customer Profitability', to: '/finance-portal/reports/customer-profitability' },
                    { label: 'KPI Dashboards', to: '/finance-portal/reports/kpi-dashboards' },
                    { label: 'Board Reports', to: '/finance-portal/reports/board-reports' },
                ],
            },
            {
                label: 'Advanced Analytics',
                icon: TimelineIcon,
                to: '/finance-portal/reports/forecasting',
                summary: 'Forecast revenue/expense with confidence bands, compute ratios, run cohort analysis, track burn/runway, benchmark against industry, and build custom reports.',
                actions: [
                    { label: 'Forecasting', to: '/finance-portal/reports/forecasting' },
                    { label: 'Financial Ratios', to: '/finance-portal/reports/financial-ratios' },
                    { label: 'Cohort Analysis', to: '/finance-portal/reports/cohort-analysis' },
                    { label: 'Burn & Runway', to: '/finance-portal/reports/burn-runway' },
                    { label: 'Industry Benchmarks', to: '/finance-portal/reports/industry-benchmarks' },
                    { label: 'Custom Report Builder', to: '/finance-portal/reports/custom-report-builder' },
                ],
            },
        ],
    },
    {
        id: 'ai',
        title: '9. AI Agents (optional)',
        description: 'Let Claude-powered agents automate repetitive work.',
        color: '#a855f7',
        icon: SmartToyIcon,
        steps: [
            {
                label: 'Transaction & Reconciliation',
                icon: SmartToyIcon,
                to: '/finance-portal/agents/categorization',
                summary: 'Agents auto-categorize, reconcile, detect anomalies, and grade data quality.',
                actions: [
                    { label: 'Categorization Agent', to: '/finance-portal/agents/categorization' },
                    { label: 'Reconciliation Agent', to: '/finance-portal/agents/reconciliation' },
                    { label: 'Anomaly Detection', to: '/finance-portal/agents/anomaly-detection' },
                    { label: 'Data Quality', to: '/finance-portal/agents/data-quality' },
                ],
            },
            {
                label: 'Forecasting & Operations',
                icon: TimelineIcon,
                to: '/finance-portal/agents/cashflow-forecasting',
                summary: 'Agents forecast cash flow, analyze budgets/vendor spend, and automate AP/AR/payroll cycles.',
                actions: [
                    { label: 'Cash-Flow Forecasting', to: '/finance-portal/agents/cashflow-forecasting' },
                    { label: 'Budget Variance', to: '/finance-portal/agents/budget-variance' },
                    { label: 'Vendor Spend', to: '/finance-portal/agents/vendor-spend' },
                    { label: 'Working Capital', to: '/finance-portal/agents/working-capital' },
                    { label: 'Month-End Close', to: '/finance-portal/agents/month-end-close' },
                    { label: 'AP Automation', to: '/finance-portal/agents/ap-automation' },
                    { label: 'AR Automation', to: '/finance-portal/agents/ar-automation' },
                    { label: 'Payroll Automation', to: '/finance-portal/agents/payroll-automation' },
                ],
            },
            {
                label: 'Command Center & Learning',
                icon: SmartToyIcon,
                to: '/finance-portal/agents/command-center',
                summary: 'Natural-language command center, document intelligence, fraud detection, and continuous improvement loops.',
                actions: [
                    { label: 'Command Center', to: '/finance-portal/agents/command-center' },
                    { label: 'Document Intelligence', to: '/finance-portal/agents/document-intelligence' },
                    { label: 'Fraud Detection', to: '/finance-portal/agents/fraud-detection' },
                    { label: 'Feedback Learning', to: '/finance-portal/agents/feedback-learning' },
                    { label: 'Agent Performance', to: '/finance-portal/agents/agent-performance' },
                    { label: 'Continuous Improvement', to: '/finance-portal/agents/continuous-improvement' },
                ],
            },
        ],
    },
];

// Quick access cards for the top of the guide
const REPORTING_QUICKLINKS = [
    { label: 'Income Statement', to: '/finance-portal/reports/income-statement', icon: TrendingUpIcon, color: '#22c55e' },
    { label: 'Balance Sheet', to: '/finance-portal/reports/balance-sheet', icon: AccountBalanceIcon, color: '#0ea5e9' },
    { label: 'Cash Flow', to: '/finance-portal/reports/cash-flow', icon: CurrencyExchangeIcon, color: '#f59e0b' },
    { label: 'Statement of Equity', to: '/finance-portal/reports/statement-of-equity', icon: PieChartIcon, color: '#8b5cf6' },
    { label: 'Trial Balance', to: '/finance-portal/reports/trial-balance', icon: ListAltIcon, color: '#6366f1' },
    { label: 'GL Detail', to: '/finance-portal/reports/gl-detail', icon: ArticleIcon, color: '#64748b' },
    { label: 'Budget vs Actual', to: '/finance-portal/reports/budget-vs-actual', icon: CompareArrowsIcon, color: '#14b8a6' },
    { label: 'Departmental P&L', to: '/finance-portal/reports/departmental-pnl', icon: BusinessCenterIcon, color: '#0d9488' },
    { label: 'Project Profitability', to: '/finance-portal/reports/project-profitability', icon: EngineeringIcon, color: '#6d28d9' },
    { label: 'Customer Profitability', to: '/finance-portal/reports/customer-profitability', icon: GroupsIcon, color: '#db2777' },
    { label: 'KPI Dashboards', to: '/finance-portal/reports/kpi-dashboards', icon: DashboardIcon, color: '#2563eb' },
    { label: 'Board Reports', to: '/finance-portal/reports/board-reports', icon: DescriptionIcon, color: '#475569' },
    { label: 'Forecasting', to: '/finance-portal/reports/forecasting', icon: TimelineIcon, color: '#16a34a' },
    { label: 'Financial Ratios', to: '/finance-portal/reports/financial-ratios', icon: CalculateIcon, color: '#0369a1' },
    { label: 'Cohort Analysis', to: '/finance-portal/reports/cohort-analysis', icon: GridOnIcon, color: '#be185d' },
    { label: 'Burn & Runway', to: '/finance-portal/reports/burn-runway', icon: SpeedIcon, color: '#dc2626' },
    { label: 'Industry Benchmarks', to: '/finance-portal/reports/industry-benchmarks', icon: EqualizerIcon, color: '#7c3aed' },
    { label: 'Custom Report Builder', to: '/finance-portal/reports/custom-report-builder', icon: BuildIcon, color: '#ea580c' },
];

export default function FinanceGuide() {
    const navigate = useNavigate();

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, fontFamily, maxWidth: 1400, mx: 'auto' }}>
            {/* Hero */}
            <Paper
                sx={{
                    p: 4, mb: 3, borderRadius: 3,
                    background: `linear-gradient(135deg, #0f172a 0%, #1e293b 100%)`,
                    color: '#fff',
                }}
            >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
                    <MenuBookIcon sx={{ fontSize: 36, color: ACCENT }} />
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.75rem' }}>
                            Finance Portal — How It Works
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: 'rgba(255,255,255,0.75)' }}>
                            Step-by-step workflow from setup to reporting. Every link jumps you to the page that performs the step.
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
                    {STAGES.map((s) => (
                        <Chip
                            key={s.id}
                            label={s.title}
                            onClick={() => {
                                document.getElementById(`stage-${s.id}`)?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            sx={{
                                fontFamily, fontWeight: 600, bgcolor: s.color, color: '#fff',
                                '&:hover': { bgcolor: s.color, opacity: 0.85 },
                                mb: 1,
                            }}
                        />
                    ))}
                </Stack>
            </Paper>

            {/* Reporting quick-jump tiles (the most-requested destination) */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                            Jump to a Report
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                            All 18 reports from Financial Reporting & Business Intelligence.
                        </Typography>
                    </Box>
                    <Button
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => document.getElementById('stage-reporting')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                        How Reporting Works
                    </Button>
                </Stack>
                <Grid container spacing={1.5}>
                    {REPORTING_QUICKLINKS.map(({ label, to, icon: Icon, color }) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={to}>
                            <Card
                                onClick={() => navigate(to)}
                                sx={{
                                    cursor: 'pointer',
                                    height: '100%',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 2,
                                    transition: 'all 0.15s',
                                    '&:hover': {
                                        borderColor: color,
                                        boxShadow: `0 2px 8px ${color}22`,
                                    },
                                }}
                                elevation={0}
                            >
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Icon sx={{ color, fontSize: 22, mb: 0.5 }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>
                                        {label}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Intro card */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.05rem', color: '#1e293b', mb: 1 }}>
                    The Big Picture
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.92rem', color: '#475569', lineHeight: 1.7, mb: 1 }}>
                    The platform turns raw financial activity into decision-ready insight through an assembly line
                    of stages. Money flows in through connected banks, gets classified into GL accounts, and
                    drives AR/AP/payroll/tax operations. At period-end, entries close, and reports materialize
                    directly from the ledger — no spreadsheet exports, no manual consolidation. AI agents sit
                    alongside each stage to automate repetitive work.
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                    <Chip label="Setup" sx={{ bgcolor: '#0ea5e9', color: '#fff', fontWeight: 600 }} size="small" />
                    <ArrowForwardIcon sx={{ color: '#94a3b8', alignSelf: 'center' }} fontSize="small" />
                    <Chip label="Transactions & GL" sx={{ bgcolor: '#f59e0b', color: '#fff', fontWeight: 600 }} size="small" />
                    <ArrowForwardIcon sx={{ color: '#94a3b8', alignSelf: 'center' }} fontSize="small" />
                    <Chip label="AR / AP / Payroll / Tax" sx={{ bgcolor: '#8b5cf6', color: '#fff', fontWeight: 600 }} size="small" />
                    <ArrowForwardIcon sx={{ color: '#94a3b8', alignSelf: 'center' }} fontSize="small" />
                    <Chip label="Close" sx={{ bgcolor: '#6366f1', color: '#fff', fontWeight: 600 }} size="small" />
                    <ArrowForwardIcon sx={{ color: '#94a3b8', alignSelf: 'center' }} fontSize="small" />
                    <Chip label="Reports & Analytics" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 600 }} size="small" />
                </Stack>
            </Paper>

            {/* Per-stage breakdown */}
            {STAGES.map((stage) => {
                const StageIcon = stage.icon;
                return (
                    <Paper
                        key={stage.id}
                        id={`stage-${stage.id}`}
                        sx={{
                            p: 0, mb: 3, borderRadius: 3,
                            border: '1px solid #e2e8f0',
                            overflow: 'hidden',
                            scrollMarginTop: 20,
                        }}
                    >
                        <Box sx={{ p: 3, bgcolor: `${stage.color}10`, borderLeft: `4px solid ${stage.color}` }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: 2,
                                    bgcolor: stage.color, color: '#fff',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <StageIcon />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.2rem', color: '#0f172a' }}>
                                        {stage.title}
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.88rem', color: '#475569' }}>
                                        {stage.description}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Box sx={{ p: 3 }}>
                            <Stepper orientation="vertical">
                                {stage.steps.map((step, idx) => {
                                    const StepIcon = step.icon;
                                    return (
                                        <Step key={step.label} active expanded>
                                            <StepLabel
                                                icon={
                                                    <Box sx={{
                                                        width: 32, height: 32, borderRadius: '50%',
                                                        bgcolor: `${stage.color}15`, color: stage.color,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <StepIcon sx={{ fontSize: 18 }} />
                                                    </Box>
                                                }
                                            >
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.98rem', color: '#0f172a' }}>
                                                    {step.label}
                                                </Typography>
                                            </StepLabel>
                                            <StepContent sx={{ borderLeft: `2px solid ${stage.color}30`, ml: '16px', pl: 2.5, pb: 3 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569', lineHeight: 1.65, mb: 1.5 }}>
                                                    {step.summary}
                                                </Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                                    {step.actions.map((a) => (
                                                        <Button
                                                            key={a.to}
                                                            size="small"
                                                            variant="outlined"
                                                            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                                                            onClick={() => navigate(a.to)}
                                                            sx={{
                                                                fontFamily,
                                                                textTransform: 'none',
                                                                borderColor: `${stage.color}60`,
                                                                color: stage.color,
                                                                mb: 1,
                                                                '&:hover': {
                                                                    borderColor: stage.color,
                                                                    bgcolor: `${stage.color}10`,
                                                                },
                                                            }}
                                                        >
                                                            {a.label}
                                                        </Button>
                                                    ))}
                                                </Stack>
                                            </StepContent>
                                        </Step>
                                    );
                                })}
                            </Stepper>
                        </Box>
                    </Paper>
                );
            })}

            {/* Typical month cadence */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.1rem', color: '#1e293b', mb: 2 }}>
                    A Typical Monthly Cadence
                </Typography>
                <Grid container spacing={2}>
                    {[
                        { when: 'Daily', what: 'Review new bank transactions, approve AI codings, send/pay invoices, watch cash dashboard.',
                          links: [
                            { label: 'Banking Center', to: '/finance-portal/banking' },
                            { label: 'Invoices', to: '/finance-portal/invoices' },
                            { label: 'Bills', to: '/finance-portal/bills' },
                            { label: 'Dashboard', to: '/finance-portal/dashboard' },
                          ] },
                        { when: 'Weekly', what: 'Review AR/AP aging, chase overdue, run payroll if applicable, pull cash-flow forecast.',
                          links: [
                            { label: 'AR Aging', to: '/finance-portal/ar-aging' },
                            { label: 'AP Aging', to: '/finance-portal/ap-aging' },
                            { label: 'Payroll Runs', to: '/finance-portal/payroll-runs' },
                            { label: 'Cash Forecast Agent', to: '/finance-portal/agents/cashflow-forecasting' },
                          ] },
                        { when: 'Month-End', what: 'Reconcile bank, approve journals, run depreciation, close the period, lock entries.',
                          links: [
                            { label: 'Reconciliation', to: '/finance-portal/agents/reconciliation' },
                            { label: 'Journal Approvals', to: '/finance-portal/journal-approvals' },
                            { label: 'Depreciation', to: '/finance-portal/depreciation' },
                            { label: 'Period Closing', to: '/finance-portal/period-closing' },
                          ] },
                        { when: 'Reporting', what: 'Pull the P&L, Balance Sheet, Cash Flow, Budget variance, KPI dashboards, and board packages.',
                          links: [
                            { label: 'Income Statement', to: '/finance-portal/reports/income-statement' },
                            { label: 'Balance Sheet', to: '/finance-portal/reports/balance-sheet' },
                            { label: 'Cash Flow', to: '/finance-portal/reports/cash-flow' },
                            { label: 'Budget vs Actual', to: '/finance-portal/reports/budget-vs-actual' },
                            { label: 'KPI Dashboards', to: '/finance-portal/reports/kpi-dashboards' },
                            { label: 'Board Reports', to: '/finance-portal/reports/board-reports' },
                          ] },
                    ].map((block) => (
                        <Grid item xs={12} md={6} key={block.when}>
                            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                                <CardContent>
                                    <Chip label={block.when} size="small"
                                        sx={{ bgcolor: ACCENT, color: '#fff', fontFamily, fontWeight: 700, mb: 1 }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569', lineHeight: 1.65, mb: 1.5 }}>
                                        {block.what}
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {block.links.map((l) => (
                                            <Button key={l.to} size="small" variant="text"
                                                onClick={() => navigate(l.to)}
                                                sx={{ fontFamily, textTransform: 'none', mb: 0.5 }}>
                                                {l.label}
                                            </Button>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: 3,
                background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)', color: '#fff' }}>
                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.1rem', mb: 1 }}>
                    Ready to Close a Period and Produce Reports?
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.9rem', mb: 2, opacity: 0.95 }}>
                    Once your transactions are coded and journal entries posted, every report below runs on demand
                    off your live general ledger.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button variant="contained" onClick={() => navigate('/finance-portal/period-closing')}
                        sx={{ bgcolor: '#fff', color: '#15803d', fontFamily, fontWeight: 700,
                            '&:hover': { bgcolor: '#f0fdf4' } }}>
                        Close the Period
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/finance-portal/reports/income-statement')}
                        sx={{ borderColor: '#fff', color: '#fff', fontFamily, fontWeight: 700,
                            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.15)' } }}>
                        Run P&L
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/finance-portal/reports/kpi-dashboards')}
                        sx={{ borderColor: '#fff', color: '#fff', fontFamily, fontWeight: 700,
                            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.15)' } }}>
                        Build KPI Dashboard
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
}
